import { AccountView, accountViewPaths } from '@daveyplate/better-auth-ui';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  useCancelSubscription,
  useSubscription,
  useSubscriptionTiers,
} from '@/hooks/useSubscription';
import { track } from '@/lib/mixpanel';

export default function AccountPage() {
  const accountFlow = useParams({
    from: '/account/$accountFlow',
    select: (params) => params.accountFlow,
  });

  const accountViewPathValues = Object.values(accountViewPaths) as string[];
  const isValidAccountPath = accountViewPathValues.includes(accountFlow);

  const { search } = useLocation();
  const error = new URLSearchParams(search).get('error');

  const navigate = useNavigate();

  // Subscription management
  const { subscription, tier, isTrialing, trialEndDate, hasPaidTier } =
    useSubscription();
  const { data: tiers } = useSubscriptionTiers();
  const cancelMutation = useCancelSubscription();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const tierInfo = tiers?.[tier];
  const tierName = tierInfo?.name ?? tier;

  const handleCancelSubscription = () => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        setShowCancelConfirm(false);
        track('SubscriptionCancelled', { previousTier: tier });
      },
    });
  };

  return (
    <div className="flex min-h-screen w-full items-start justify-center bg-neutral-50 pt-10">
      <div className="mb-10 w-full max-w-4xl space-y-6 px-4">
        <div className="relative flex items-center justify-center">
          <div className="absolute left-0">
            <Button
              variant="outline"
              onClick={() => {
                track('ButtonClick', { name: 'BackToHome' });
                navigate({ to: '/' });
              }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Înapoi la pagina principală
            </Button>
          </div>
          <div
            className="font-logo cursor-pointer text-3xl font-medium leading-relaxed tracking-tight text-neutral-900"
            onClick={() => {
              navigate({ to: '/' });
            }}>
            SolomonQ
          </div>
        </div>
        {error && (
          <div className="rounded border-2 border-red-300 bg-red-100 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="rounded-lg border-2 border-neutral-200 bg-white p-6 shadow-sm">
          <AccountView
            pathname={isValidAccountPath ? accountFlow : undefined}
            className="gap-6"
            classNames={{
              cards: 'grid w-full gap-6',
              sidebar: {
                buttonActive: 'bg-neutral-100 text-neutral-900',
                button: 'text-neutral-700',
              },
              card: {
                base: 'border border-neutral-200 shadow-none',
                footer: 'pt-3',
              },
            }}
          />
        </div>

        {/* Subscription Management */}
        {subscription && (
          <div className="rounded-lg border-2 border-neutral-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-neutral-600" />
              <h2 className="text-lg font-semibold text-neutral-900">
                Abonament
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-600">Planul curent</p>
                  <p className="text-lg font-medium text-neutral-900">
                    {tierName}
                    {isTrialing && (
                      <span className="ml-2 text-sm font-normal text-green-600">
                        (perioadă de încercare)
                      </span>
                    )}
                  </p>
                  {isTrialing && trialEndDate && (
                    <p className="text-sm text-neutral-500">
                      Perioada de încercare expiră pe{' '}
                      {trialEndDate.toLocaleDateString('ro-RO')}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: '/upgrade' })}>
                  {hasPaidTier ? 'Schimbă planul' : 'Upgrade'}
                </Button>
              </div>

              {hasPaidTier && !showCancelConfirm && (
                <div className="border-t border-neutral-200 pt-4">
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-sm text-red-600 hover:text-red-700 hover:underline">
                    Anulează abonamentul
                  </button>
                </div>
              )}

              {showCancelConfirm && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="mb-3 text-sm text-red-800">
                    Ești sigur că vrei să anulezi abonamentul? Vei pierde
                    accesul la alertele AI.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleCancelSubscription}
                      disabled={cancelMutation.isPending}>
                      {cancelMutation.isPending
                        ? 'Se anulează...'
                        : 'Da, anulează'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCancelConfirm(false)}>
                      Renunță
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
